import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Layout } from "../components/Layout";
import { api } from "../api/client";
import { formatDateTime } from "../lib/workorders";
import {
  INCIDENT_SEVERITIES,
  INCIDENT_STATUSES,
  formatIncidentId,
  incidentStatusBadge,
  incidentStatusLabel,
  incidentTypeLabel,
  severityBadge,
  severityLabel,
} from "../lib/incidents";

const COLUMNS = ["ID", "When", "Building", "Tenant", "Phone", "Type", "Severity", "Status"];

export function IncidentsPage() {
  const [building, setBuilding] = useState("all");
  const [status, setStatus] = useState("all");
  const [severity, setSeverity] = useState("all");

  const buildingsQ = useQuery({
    queryKey: ["ref", "buildings"],
    queryFn: () => api.reference.buildings(),
  });
  const listQ = useQuery({
    queryKey: ["incidents", { building, status, severity }],
    queryFn: () => api.incidents.list({ building, status, severity }),
  });

  const rows = listQ.data?.incidents ?? [];

  return (
    <Layout title="Incidents">
      <div className="page-actions">
        <Link to="/incidents/new" className="btn btn-primary btn-xl">
          + Add an incident
        </Link>
      </div>

      <div className="filters">
        <label className="field">
          <span>Building</span>
          <select value={building} onChange={(e) => setBuilding(e.target.value)}>
            <option value="all">All buildings</option>
            {(buildingsQ.data?.buildings ?? []).map((b) => (
              <option key={b.id} value={String(b.id)}>
                {b.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="all">All</option>
            {INCIDENT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {incidentStatusLabel(s)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Severity</span>
          <select value={severity} onChange={(e) => setSeverity(e.target.value)}>
            <option value="all">All</option>
            {INCIDENT_SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {severityLabel(s)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <section className="card">
        {listQ.isLoading ? (
          <p className="sub">Loading…</p>
        ) : listQ.isError ? (
          <p className="sub">Could not load incidents. Please try again.</p>
        ) : rows.length === 0 ? (
          <p className="sub">No incidents match these filters.</p>
        ) : (
          <div className="table-wrap stack-md">
            <table>
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th key={col}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((i) => (
                  <tr key={i.id}>
                    <td data-label="ID">
                      <Link to={`/incidents/${i.id}`} className="wo-title-link">
                        {formatIncidentId(i.id)}
                      </Link>
                    </td>
                    <td data-label="When">{formatDateTime(i.incidentDatetime)}</td>
                    <td data-label="Building">{i.buildingName}</td>
                    <td data-label="Tenant">{i.tenantName ?? "—"}</td>
                    <td data-label="Phone">{i.reporterPhone ?? "—"}</td>
                    <td data-label="Type">{incidentTypeLabel(i.incidentType)}</td>
                    <td data-label="Severity">
                      <span className={`badge badge-${severityBadge(i.severity)}`}>
                        {severityLabel(i.severity)}
                      </span>
                    </td>
                    <td data-label="Status">
                      <span className={`badge badge-${incidentStatusBadge(i.status)}`}>
                        {incidentStatusLabel(i.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </Layout>
  );
}
